import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Input, Radio, Select, Typography } from 'antd';

import AlertComponent from 'components/Alert';
import CharacterCounter from 'components/CharacterCounter';
import CropUploader from 'components/CropUploader';
import { removeDoubleSpace } from 'helpers/functions';
import { EChargeType } from 'interfaces/IEditProduct';
import { EPartnerCode } from 'interfaces/IProduct';
import type IProduct from 'interfaces/IProduct';
import { FormContent } from 'scenes/Producer/Product/EditProduct/components/FormContent';
import { EProductType } from 'v2/interfaces/product';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { run } from 'validations/ruleRunner';

import Validations from './validations';

interface IProps {
  product: IProduct;
  handleChange: (...args: any[]) => any;
  categoryList: Array<any>;
  setRefField: any;
  setRefError: any;
  validateTitleForSave: any;
  productTitleExists: boolean;
  enabledValidation: boolean;
  categorySelected?: any;
  hasDeliveries?: boolean;
  resetDeliveries?: () => void;
  handleProductType?: (...args: any[]) => any;
}

function General(props: IProps) {
  const {
    handleChange,
    product,
    categoryList,
    setRefField,
    validateTitleForSave,
    enabledValidation,
    hasDeliveries,
    resetDeliveries,
    handleProductType
  } = props;

  const [validationErrors, setValidationErrors] = useState<Array<any>>([]);
  const prevPropsRef = useRef({ enabledValidation, product });

  const errorFor = useCallback((field: any) => validationErrors[field] || '', [validationErrors]);
  const isInvalid = useCallback((field: any) => !!validationErrors[field], [validationErrors]);

  const handleProductTypeChange = useCallback(
    async (e: any) => {
      if (hasDeliveries) {
        const confirm = await AlertComponent.confirm({
          title: 'Atenção',
          message: 'Ao alterar o tipo de produto, você perderá todas as entregas cadastradas. Deseja continuar?'
        });
        if (confirm) resetDeliveries!();
      }

      handleProductType!('type', e.target.value);
    },
    [hasDeliveries, resetDeliveries, handleProductType]
  );

  useEffect(() => {
    const prev = prevPropsRef.current;
    const shouldRun =
      (prev.enabledValidation === false && enabledValidation) || (enabledValidation && product !== prev.product);
    if (shouldRun) setValidationErrors(run(product, Validations));
    prevPropsRef.current = { enabledValidation, product };
  }, [enabledValidation, product]);

  const isService = product.cnt_partner_cod === EPartnerCode.SERVICE || product.type === EProductType.SERVICE;
  const categories = Array.isArray(categoryList) ? categoryList : [];
  const isSubscription = product.charge_type === EChargeType.SUBSCRIPTION;
  const isBlinket = product.cnt_partner_cod === EPartnerCode.BLINKET;
  const userHasAccessToSubscriptionModule = eduzzAccountsService.verifyUserHasBetaModule('planos_assinatura');

  function formTitle() {
    if (isSubscription) {
      return 'Dados básicos da assinatura';
    }

    if (isService) {
      return 'Dados básicos do serviço';
    }

    return 'Dados básicos do produto';
  }

  return (
    <FormContent title={formTitle()} loading={false}>
      <div className='flex flex-col gap-4'>
        <div>
          {isSubscription && (userHasAccessToSubscriptionModule || !!handleProductType) && (
            <>
              <Typography.Text className='mb-2 block font-medium'>
                Você está criando um plano de assinatura para um produto ou serviço?
              </Typography.Text>
              <Radio.Group
                onChange={handleProductTypeChange}
                value={
                  product.type === EProductType.DIGITAL
                    ? 'product'
                    : product.type === EProductType.SERVICE
                      ? 'service'
                      : undefined
                }
              >
                <Radio id='new-product-type-product-radio-button' value='product'>
                  Produto digital
                </Radio>
                <Radio id='new-product-type-service-radio-button' value='service'>
                  Serviço
                </Radio>
              </Radio.Group>
              {isInvalid('type') && (
                <Typography.Text className='mt-1 block text-xs text-(--uizz-antd-color-error)'>
                  Selecione o tipo de produto
                </Typography.Text>
              )}
            </>
          )}

          <div className='relative z-10 my-4'>
            <Typography.Text className={`mb-1 block ${isInvalid('title') ? 'text-(--uizz-antd-color-error)' : ''}`}>
              {isService ? 'Título do Serviço' : 'Título do produto'}
              <Typography.Text  className='text-(--uizz-antd-color-error)'>*</Typography.Text >
            </Typography.Text>
            <Input
              id='new-product-title'
              status={isInvalid('title') ? 'error' : undefined}
              value={product.title}
              onBlur={(e: any) => validateTitleForSave(e.target.value)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('title', e.target.value)}
              ref={(ref: any) => setRefField('title', ref)}
              placeholder='Ex: Certificação Especialista em Tráfego Pago'
            />
            <Typography.Text
              className={`mt-1 block ${isInvalid('title') ? 'text-(--uizz-antd-color-error)' : 'text-content-subtitle'}`}
            >
              {isInvalid('title')
                ? errorFor('title')
                : `O título do seu ${isService ? 'serviço' : 'produto'} deve ser condizente com o que vai ser entregue`}
            </Typography.Text>
          </div>

          <CharacterCounter
            value={product.description}
            limits={4000}
            minimum={100}
            id='new-product-description'
            error={isInvalid('description')}
            ref={(ref: any) => setRefField('description', ref)}
            helperText={
              isInvalid('description')
                ? errorFor('description')
                : 'A descrição do ' +
                  (isService ? 'serviço' : 'produto') +
                  ' deve deixar claro do que se trata seu ' +
                  (isService ? 'serviço' : 'produto')
            }
            label={isService ? 'Descrição do serviço' : 'Descrição do produto'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange('description', removeDoubleSpace(e.target.value))
            }
            singleSpace={true}
            placeholder='Ex: Certificação Especialista em Tráfego Pago. Você irá aprender o método testado e aprovado por centenas de empresários para gerar novos clientes para o seu negócio todos os dias.'
          />
        </div>
        <div>
          <Typography.Text
            className={`mb-1 block ${isInvalid('categories') ? 'text-(--uizz-antd-color-error)' : ''}`}
          >
            Categorias<Typography.Text  className='text-(--uizz-antd-color-error)'>*</Typography.Text >
          </Typography.Text>
          <Select
            id='new-product-categories'
            status={isInvalid('categories') ? 'error' : undefined}
            value={product.getIdCategories()}
            onChange={(value: any) => handleChange('categories', value)}
            placeholder='Informe uma ou mais categorias'
            mode='multiple'
            showSearch
            optionFilterProp='label'
            className='w-full'
            options={categories.map((item: any) => ({ label: item.name, value: item.id }))}
          />
          {isInvalid('categories') && (
            <Typography.Text className='mt-1 block text-(--uizz-antd-color-error)'>
              {errorFor('categories')}
            </Typography.Text>
          )}
        </div>
        <div>
          <Typography.Text
            className={`mb-1 block ${isInvalid('image_id') ? 'text-(--uizz-antd-color-error)' : ''}`}
          >
            Imagem do {isService ? 'Serviço' : 'produto'}<Typography.Text  className='text-(--uizz-antd-color-error)'>*</Typography.Text >
          </Typography.Text>
          <CropUploader
            id='new-product-image'
            name='image'
            fileName='image_id'
            uploadType='cnt_foto'
            onChange={handleChange}
          />
          <Typography.Text
            className={`mt-1 block ${errorFor('image_id') ? 'text-(--uizz-antd-color-error)' : 'text-content-subtitle'}`}
          >
            {isInvalid('image_id')
              ? errorFor('image_id')
              : isService
                ? 'A imagem deve ter boa qualidade e representar o serviço que está sendo oferecido.'
                : 'A imagem deve ter boa qualidade e representar o que está sendo vendido.'}
          </Typography.Text>
        </div>
        <div className='relative z-10 my-4'>
          <Typography.Text className={`mb-1 block ${errorFor('support_email') ? 'text-(--uizz-antd-color-error)' : ''}`}>E-mail de suporte<Typography.Text  className='text-(--uizz-antd-color-error)'>*</Typography.Text ></Typography.Text>
          <Input
            id='support_email'
            status={isInvalid('support_email') ? 'error' : undefined}
            value={product.support_email}
            disabled={isBlinket}
            onChange={(e: any) => handleChange('support_email', e.target.value)}
          />
          <Typography.Text
            className={`mt-1 block ${isInvalid('support_email') ? 'text-(--uizz-antd-color-error)' : 'text-content-subtitle'}`}
          >
            {isInvalid('support_email')
              ? errorFor('support_email')
              : 'E-mail que será exibido para seus compradores e que receberá os e-mails de contato do suporte.'}
          </Typography.Text>
        </div>
      </div>
    </FormContent>
  );
}

export default General;
